import {
  Badge,
  Body1,
  Button,
  Checkbox,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Text,
  Textarea,
  Title3,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  PAY_EXCEPTION_FIELDS,
  payExceptionInputFromWire,
  payExceptionSummary,
  validatePayException,
  type Department,
  type EarningKind,
  type EmployeeAllocations,
  type NsPayrollItem,
  type PayException,
  type PayExceptionField,
  type PayExceptionWire,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import type { EmployeeRow } from './api';
import { DataTable } from './components/DataTable';
import type { DataColumn } from './components/DataTable';
import { SideBlade } from './components/SideBlade';
import { toErrorMessage } from './errors';
import { toPayrollOptions } from './payrollOptions';

/** Entry kinds an exception can be scoped to; mirrors the CHECK on app.pay_exception. */
const ENTRY_KINDS: readonly EarningKind[] = [
  'wage',
  'overtime',
  'pto',
  'holiday',
  'bonus',
  'reimbursement',
  'salary',
  'other',
];

/** What each field overrides, spelled out where the admin chooses it. */
const FIELD_LABEL: Record<PayExceptionField, string> = {
  earning: 'Earning type — post under a specific NetSuite earning',
  overtime: 'Overtime — exempt, or a different weekly limit',
  rate: 'Rate — a specific hourly rate',
  commission_split: 'Commission split — send a share to another employee',
};

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    // Every ancestor between the page and a table's scroller must be able to
    // shrink, or the overflow lands on the document instead. See responsive.ts.
    ...SHRINKABLE,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    // Title + action buttons cannot share one line on a phone. Without wrapping,
    // this row is wider than the screen and — being a non-scrolling child — it
    // drags the whole content region sideways, clipping everything's left edge
    // (Michael, 2026-09-10: "Employees" rendered as "ees").
    flexWrap: 'wrap',
    ...SHRINKABLE,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  rowActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
  banner: {
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

/**
 * A DEPARTMENT SPLIT, shown here read-only.
 *
 * These are not `pay_exception` rows — they live in employee_department_allocation
 * and are edited per-employee on the Employees tab. They appear on THIS tab anyway
 * because a tab called "Pay exceptions" that shows one of the five rules Michael
 * actually gave us is a tab that lies: four of the five are department splits, and
 * he opened this page expecting to see all of them (2026-09-10). Editing stays
 * where the mechanism lives; only the VIEW is unified.
 */
interface DeptSplit {
  employeeId: string;
  employeeName: string;
  payType: string;
  shares: Array<{ name: string; percent: number }>;
}

/** One grid row: either a real pay exception, or a department split. */
type Row =
  | { kind: 'exception'; id: string; ex: PayException }
  | { kind: 'split'; id: string; split: DeptSplit };

/** A blank form: a rate exception on nobody, active, employee-wide. */
function blankForm(): PayExceptionWire {
  return {
    employeeId: '',
    field: 'rate',
    entryKind: null,
    nsDepartmentId: null,
    nsPayrollItemId: null,
    overtimeExempt: null,
    weeklyRegularHoursLimit: null,
    hourlyRate: null,
    transferToEmployeeId: null,
    transferPercent: null,
    note: '',
    active: true,
  };
}

/** Load an existing exception back into the flat form shape. */
function toForm(ex: PayException): PayExceptionWire {
  return {
    employeeId: ex.employeeId,
    field: ex.rule.field,
    entryKind: ex.entryKind,
    nsDepartmentId: ex.nsDepartmentId,
    nsPayrollItemId: ex.rule.field === 'earning' ? ex.rule.nsPayrollItemId : null,
    overtimeExempt: ex.rule.field === 'overtime' ? ex.rule.exempt : null,
    weeklyRegularHoursLimit: ex.rule.field === 'overtime' ? ex.rule.weeklyRegularHoursLimit : null,
    hourlyRate: ex.rule.field === 'rate' ? ex.rule.hourlyRate : null,
    transferToEmployeeId: ex.rule.field === 'commission_split' ? ex.rule.toEmployeeId : null,
    transferPercent: ex.rule.field === 'commission_split' ? ex.rule.percent : null,
    note: ex.note,
    active: ex.active,
  };
}

/**
 * PAY EXCEPTIONS (Administrator-only) — the standing special pay rules that override
 * the normal payroll rules for one named person.
 *
 * The two rules from Michael (2026-09-02) are visible in the UI, not just the model:
 * there is NO date picker, because an exception stands until it is deactivated or
 * deleted; and the form names ONE field at a time, because an exception overrides
 * only the field it names — someone needing two things changed gets two rows.
 *
 * The form runs `validatePayException` from @worklogix/shared before submitting, so
 * it refuses exactly what the API refuses, with the same wording, instead of
 * round-tripping to discover it.
 */
export function ExceptionsTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();
  const navigate = useNavigate();

  const [exceptions, setExceptions] = useState<PayException[]>([]);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [payrollItems, setPayrollItems] = useState<NsPayrollItem[]>([]);
  const [splits, setSplits] = useState<DeptSplit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit blade. `editing` null with the blade open = adding a new one.
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PayException | null>(null);
  const [form, setForm] = useState<PayExceptionWire>(blankForm());
  const [saving, setSaving] = useState(false);
  const [bladeError, setBladeError] = useState<string | null>(null);

  const reload = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void api
      .listPayExceptions()
      .then((rows) => {
        if (active) {
          setExceptions(rows);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => reload(), [reload]);

  /**
   * The department splits, read from the employees who have one. One request per
   * split-enabled employee — a handful of people, not the whole roster, because
   * `splitEnabled` is already on the employee list and filters it down first.
   *
   * Non-fatal per employee: one failed allocation read drops that row rather than
   * emptying the table, since the pay exceptions above it are unaffected.
   */
  useEffect(() => {
    const targets = employees.filter((e) => e.splitEnabled);
    if (targets.length === 0) {
      setSplits([]);
      return;
    }
    let active = true;
    const deptName = new Map(departments.map((d) => [d.id, d.name]));
    void Promise.all(
      targets.map((e) =>
        api
          .getEmployeeAllocations(e.id)
          .then((a) => ({ e, a }))
          .catch(() => null)
      )
    ).then((results) => {
      if (!active) {
        return;
      }
      setSplits(
        results
          .filter((r): r is { e: EmployeeRow; a: EmployeeAllocations } => r !== null)
          .map(({ e, a }) => ({
            employeeId: e.id,
            employeeName: e.displayName,
            payType: e.payType,
            shares: a.allocations
              .filter((x) => x.percent !== null)
              // Largest share first: it is the one a reader cares about.
              .sort((x, y) => (y.percent ?? 0) - (x.percent ?? 0))
              .map((x) => ({
                name: deptName.get(x.departmentId) ?? 'Unknown department',
                percent: x.percent ?? 0,
              })),
          }))
          .filter((s) => s.shares.length > 0)
      );
    });
    return () => {
      active = false;
    };
  }, [api, employees, departments]);

  // Pickers. Each is non-fatal: the grid is the point of the page and must still
  // render if one of these fails.
  useEffect(() => {
    let active = true;
    void api
      .listEmployees()
      .then((rows) => active && setEmployees(rows))
      .catch(() => active && setEmployees([]));
    void api
      .listDepartments()
      .then((rows) => active && setDepartments(rows))
      .catch(() => active && setDepartments([]));
    void api
      .listPayrollItems()
      .then((rows) => active && setPayrollItems(rows))
      .catch(() => active && setPayrollItems([]));
    return () => {
      active = false;
    };
  }, [api]);

  const payrollOptions = useMemo(() => toPayrollOptions(payrollItems), [payrollItems]);
  const employeeName = useCallback(
    (id: string): string => employees.find((e) => e.id === id)?.displayName ?? id,
    [employees]
  );
  /** Only departments with a NetSuite id can scope a rule — the id is what matches. */
  const scopeDepartments = useMemo(
    () => departments.filter((d) => d.nsDepartmentId !== null && d.nsDepartmentId !== ''),
    [departments]
  );
  const deptLabel = useCallback(
    (nsId: string): string =>
      scopeDepartments.find((d) => d.nsDepartmentId === nsId)?.name ?? `NS #${nsId}`,
    [scopeDepartments]
  );

  const openAdd = useCallback(() => {
    setEditing(null);
    setForm(blankForm());
    setBladeError(null);
    setOpen(true);
  }, []);

  const openEdit = useCallback((ex: PayException) => {
    setEditing(ex);
    setForm(toForm(ex));
    setBladeError(null);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setEditing(null);
    setForm(blankForm());
    setSaving(false);
    setBladeError(null);
  }, []);

  const patch = useCallback((changes: Partial<PayExceptionWire>) => {
    setForm((prev) => ({ ...prev, ...changes }));
  }, []);

  /**
   * Switching field CLEARS the other fields' values and, for overtime, the scope.
   * Carrying a stale rate into an overtime rule would build a payload the table's
   * CHECK constraints reject — and an overtime rule is always employee-wide.
   */
  const changeField = useCallback((field: PayExceptionField) => {
    setForm((prev) => ({
      ...prev,
      field,
      nsPayrollItemId: null,
      overtimeExempt: field === 'overtime' ? true : null,
      weeklyRegularHoursLimit: null,
      hourlyRate: null,
      transferToEmployeeId: null,
      transferPercent: null,
      // A commission is not clocked at a department, so like overtime it cannot be
      // scoped — clear the scope rather than show controls the API would refuse.
      entryKind: field === 'overtime' || field === 'commission_split' ? null : prev.entryKind,
      nsDepartmentId:
        field === 'overtime' || field === 'commission_split' ? null : prev.nsDepartmentId,
    }));
  }, []);

  /** The shared rule set, run before submitting so the UI refuses what the API would. */
  const formProblem = useMemo((): string | null => {
    if (form.employeeId === '') {
      return 'Choose an employee.';
    }
    const input = payExceptionInputFromWire(form);
    if (input === null) {
      return `Give the ${form.field} exception its value.`;
    }
    return validatePayException(input);
  }, [form]);

  const handleSave = useCallback(async () => {
    if (formProblem !== null) {
      setBladeError(formProblem);
      return;
    }
    setSaving(true);
    setBladeError(null);
    try {
      if (editing === null) {
        await api.createPayException(form);
      } else {
        await api.updatePayException(editing.id, form);
      }
      close();
      reload();
    } catch (err: unknown) {
      setBladeError(toErrorMessage(err));
      setSaving(false);
    }
  }, [api, editing, form, formProblem, close, reload]);

  /** Deactivate/reactivate in place — the reversible way to stop a rule. */
  const toggleActive = useCallback(
    async (ex: PayException) => {
      setError(null);
      try {
        await api.updatePayException(ex.id, { ...toForm(ex), active: !ex.active });
        reload();
      } catch (err: unknown) {
        setError(toErrorMessage(err));
      }
    },
    [api, reload]
  );

  const handleDelete = useCallback(
    async (ex: PayException) => {
      setError(null);
      try {
        await api.deletePayException(ex.id);
        reload();
      } catch (err: unknown) {
        setError(toErrorMessage(err));
      }
    },
    [api, reload]
  );

  /** One line naming the shares, with the whole split on hover. */
  const splitSummary = useCallback((s: DeptSplit): { label: string; detail: string } => {
    const parts = s.shares.map((x) => `${x.percent}% ${x.name}`);
    const shown = parts.slice(0, 2);
    const extra = parts.length - shown.length;
    return {
      label: extra > 0 ? `${shown.join(', ')} +${extra} more` : shown.join(', '),
      detail: parts.join('\n'),
    };
  }, []);

  const columns: ReadonlyArray<DataColumn<Row>> = useMemo(
    () => [
      {
        columnId: 'employee',
        header: 'Employee',
        renderCell: (r) => (r.kind === 'exception' ? r.ex.employeeName : r.split.employeeName),
      },
      {
        columnId: 'field',
        header: 'Overrides',
        renderCell: (r) =>
          r.kind === 'exception' ? (
            <Badge appearance="tint" color="brand">
              {r.ex.rule.field}
            </Badge>
          ) : (
            <Badge appearance="tint" color="informative">
              departments
            </Badge>
          ),
      },
      {
        columnId: 'rule',
        header: 'Rule',
        renderCell: (r) => {
          if (r.kind === 'exception') {
            return <Text>{payExceptionSummary(r.ex)}</Text>;
          }
          const { label, detail } = splitSummary(r.split);
          return (
            <Tooltip content={detail} relationship="label">
              <Text>{label}</Text>
            </Tooltip>
          );
        },
      },
      {
        columnId: 'scope',
        header: 'Applies to',
        renderCell: (r) => {
          if (r.kind !== 'exception') {
            // A department split divides ALL of this person's pay, not a slice of it.
            return <Text>All pay</Text>;
          }
          const parts: string[] = [];
          if (r.ex.entryKind !== null) {
            parts.push(r.ex.entryKind);
          }
          if (r.ex.nsDepartmentId !== null) {
            parts.push(deptLabel(r.ex.nsDepartmentId));
          }
          return <Text>{parts.length === 0 ? 'Every entry' : parts.join(' · ')}</Text>;
        },
      },
      {
        columnId: 'note',
        header: 'Why',
        renderCell: (r) => {
          const note =
            r.kind === 'exception'
              ? r.ex.note
              : `${r.split.payType === 'salary' ? 'Salary' : 'Hours'} divided across departments`;
          return (
            <Tooltip content={note} relationship="label">
              <Text truncate wrap={false}>
                {note}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        columnId: 'active',
        header: 'Status',
        renderCell: (r) => {
          // A split only appears here when splitEnabled is on, so it is always active.
          if (r.kind !== 'exception' || r.ex.active) {
            return (
              <Badge appearance="tint" color="success">
                Active
              </Badge>
            );
          }
          return (
            <Tooltip content="Kept for the record but NOT applied to pay." relationship="label">
              <Badge appearance="tint" color="subtle">
                Inactive
              </Badge>
            </Tooltip>
          );
        },
      },
      {
        columnId: 'updated',
        header: 'Last changed',
        renderCell: (r) =>
          r.kind === 'exception' ? (
            <Tooltip content={`by ${r.ex.updatedBy}`} relationship="label">
              <Text>{r.ex.updatedAt.slice(0, 10)}</Text>
            </Tooltip>
          ) : (
            // employee_department_allocation keeps no audit columns, so there is
            // nothing honest to show here.
            <Text>—</Text>
          ),
      },
      {
        columnId: 'actions',
        header: '',
        renderCell: (r) =>
          r.kind === 'exception' ? (
            <span className={styles.rowActions}>
              <Button size="small" onClick={() => openEdit(r.ex)}>
                Edit
              </Button>
              <Button size="small" appearance="subtle" onClick={() => void toggleActive(r.ex)}>
                {r.ex.active ? 'Deactivate' : 'Reactivate'}
              </Button>
              <Button size="small" appearance="subtle" onClick={() => void handleDelete(r.ex)}>
                Delete
              </Button>
            </span>
          ) : (
            // Read-only here ON PURPOSE: the split editor lives on the Employees tab,
            // and two places to edit one thing is how they drift apart.
            <Button size="small" onClick={() => void navigate('/admin/employees')}>
              Edit on Employees
            </Button>
          ),
      },
    ],
    [deptLabel, openEdit, toggleActive, handleDelete, splitSummary, navigate, styles.rowActions]
  );

  /**
   * Pay exceptions first, then department splits, each alphabetical. Exceptions are
   * the editable ones and the ones this tab owns; the splits are here so the page
   * answers "who is paid differently?" in full rather than for one of five people.
   */
  const rows: Row[] = useMemo(
    () => [
      ...exceptions.map((ex): Row => ({ kind: 'exception', id: `x:${ex.id}`, ex })),
      ...[...splits]
        .sort((a, b) => a.employeeName.localeCompare(b.employeeName))
        .map((split): Row => ({ kind: 'split', id: `s:${split.employeeId}`, split })),
    ],
    [exceptions, splits]
  );

  const activeCount = exceptions.filter((e) => e.active).length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title3>Pay exceptions</Title3>
        <Button appearance="primary" onClick={openAdd}>
          Add exception
        </Button>
      </div>

      <Body1>
        Every employee who is paid differently from the standard rules. A pay exception takes
        precedence over normal payroll, overrides only the field it names, and stands until you
        deactivate or delete it — there are no start or end dates. Department splits are shown
        here too, read-only: they divide a person's pay across departments and are edited on the
        Employees tab. {activeCount} exception{activeCount === 1 ? '' : 's'} and {splits.length}{' '}
        department split{splits.length === 1 ? '' : 's'}.
      </Body1>

      {error !== null ? (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <Spinner label="Loading exceptions…" />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          emptyMessage="No pay exceptions or department splits. Everyone is paid by the standard rules."
        />
      )}

      <SideBlade
        open={open}
        onClose={close}
        title={editing === null ? 'Add pay exception' : `Edit exception — ${editing.employeeName}`}
      >
        <form
          className={styles.form}
          onSubmit={(ev) => {
            ev.preventDefault();
            void handleSave();
          }}
        >
          <Field
            label="Employee"
            required
            // Spread rather than `hint={undefined}`: the workspace runs
            // exactOptionalPropertyTypes, so an explicit undefined is not assignable.
            {...(editing === null
              ? {}
              : {
                  hint: 'The employee cannot be changed — moving an override to someone else has to be a delete and a re-create.',
                })}
          >
            <Dropdown
              disabled={saving || editing !== null}
              value={form.employeeId === '' ? '' : employeeName(form.employeeId)}
              selectedOptions={form.employeeId === '' ? [] : [form.employeeId]}
              onOptionSelect={(_, d) => patch({ employeeId: d.optionValue ?? '' })}
            >
              {employees.map((e) => (
                <Option key={e.id} value={e.id} text={e.displayName}>
                  {e.displayName}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field
            label="What it overrides"
            required
            hint="One field per exception. Someone needing two things changed gets two exceptions."
          >
            <Dropdown
              disabled={saving}
              value={FIELD_LABEL[form.field]}
              selectedOptions={[form.field]}
              onOptionSelect={(_, d) => changeField((d.optionValue ?? 'rate') as PayExceptionField)}
            >
              {PAY_EXCEPTION_FIELDS.map((f) => (
                <Option key={f} value={f} text={FIELD_LABEL[f]}>
                  {FIELD_LABEL[f]}
                </Option>
              ))}
            </Dropdown>
          </Field>

          {form.field === 'earning' ? (
            <Field label="Post under this earning type" required>
              <Dropdown
                disabled={saving}
                value={payrollOptions.find((o) => o.value === form.nsPayrollItemId)?.label ?? ''}
                selectedOptions={form.nsPayrollItemId === null ? [] : [form.nsPayrollItemId]}
                onOptionSelect={(_, d) => patch({ nsPayrollItemId: d.optionValue ?? null })}
              >
                {payrollOptions.map((o) => (
                  <Option key={o.value} value={o.value} text={o.label}>
                    {o.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          ) : null}

          {form.field === 'overtime' ? (
            <>
              <Field
                label="Overtime"
                hint="Exempt = no overtime however many hours are worked. Otherwise overtime starts above the limit you give instead of the standard 40."
              >
                <Checkbox
                  disabled={saving}
                  checked={form.overtimeExempt === true}
                  label="Exempt from overtime"
                  onChange={(_, d) =>
                    patch({
                      overtimeExempt: d.checked === true,
                      weeklyRegularHoursLimit: null,
                    })
                  }
                />
              </Field>
              {form.overtimeExempt === true ? null : (
                <Field
                  label="Weekly regular-hours limit"
                  required
                  hint="Hours per payroll week (Wed–Tue) before overtime starts."
                >
                  <Input
                    disabled={saving}
                    type="number"
                    inputMode="decimal"
                    value={
                      form.weeklyRegularHoursLimit === null
                        ? ''
                        : String(form.weeklyRegularHoursLimit)
                    }
                    onChange={(_, d) =>
                      patch({
                        weeklyRegularHoursLimit: d.value === '' ? null : Number.parseFloat(d.value),
                      })
                    }
                  />
                </Field>
              )}
            </>
          ) : null}

          {form.field === 'rate' ? (
            <Field label="Hourly rate" required>
              <Input
                disabled={saving}
                type="number"
                inputMode="decimal"
                value={form.hourlyRate === null ? '' : String(form.hourlyRate)}
                onChange={(_, d) =>
                  patch({ hourlyRate: d.value === '' ? null : Number.parseFloat(d.value) })
                }
              />
            </Field>
          ) : null}

          {form.field === 'commission_split' ? (
            <>
              <Field
                label="Send the share to"
                required
                hint="This employee gets a matching commission entry whenever the earner's commission is entered."
              >
                <Dropdown
                  disabled={saving}
                  value={
                    form.transferToEmployeeId === null
                      ? ''
                      : employeeName(form.transferToEmployeeId)
                  }
                  selectedOptions={
                    form.transferToEmployeeId === null ? [] : [form.transferToEmployeeId]
                  }
                  onOptionSelect={(_, d) =>
                    patch({ transferToEmployeeId: d.optionValue ?? null })
                  }
                >
                  {employees
                    // Never the earner themselves — the API and the database both
                    // refuse it, so it should not be offerable.
                    .filter((e) => e.id !== form.employeeId)
                    .map((e) => (
                      <Option key={e.id} value={e.id} text={e.displayName}>
                        {e.displayName}
                      </Option>
                    ))}
                </Dropdown>
              </Field>
              <Field
                label="Share sent"
                required
                hint="The percentage that LEAVES the earner. The rest stays with them."
              >
                <Input
                  disabled={saving}
                  type="number"
                  inputMode="decimal"
                  step={0.01}
                  min={0.01}
                  max={99.99}
                  contentAfter="%"
                  value={form.transferPercent === null ? '' : String(form.transferPercent)}
                  onChange={(_, d) =>
                    patch({
                      transferPercent: d.value === '' ? null : Number.parseFloat(d.value),
                    })
                  }
                />
              </Field>
              {form.transferPercent === null ? null : (
                <Body1>
                  {`${(100 - form.transferPercent).toFixed(2)}% stays with the earner, ${form.transferPercent.toFixed(2)}% goes to the other employee.`}
                </Body1>
              )}
            </>
          ) : null}

          {/*
            Overtime is decided across a whole week, and a week can span two
            departments — so it cannot be scoped, and the scope controls are hidden
            rather than shown-and-rejected.
          */}
          {form.field === 'overtime' || form.field === 'commission_split' ? (
            <Body1>
              {form.field === 'overtime'
                ? 'Applies to the whole employee — overtime is a weekly rule.'
                : 'Applies to the whole employee — a commission is not clocked at a department.'}
            </Body1>
          ) : (
            <>
              <Field label="Only for this entry kind" hint="Leave as “Every kind” to apply to all.">
                <Dropdown
                  disabled={saving}
                  value={form.entryKind ?? 'Every kind'}
                  selectedOptions={[form.entryKind ?? '__any']}
                  onOptionSelect={(_, d) =>
                    patch({
                      entryKind:
                        d.optionValue === '__any' ? null : ((d.optionValue ?? null) as EarningKind),
                    })
                  }
                >
                  <Option value="__any" text="Every kind">
                    Every kind
                  </Option>
                  {ENTRY_KINDS.map((k) => (
                    <Option key={k} value={k} text={k}>
                      {k}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              <Field
                label="Only for this department"
                hint="Leave as “Every department” to apply to all. Only departments mapped to NetSuite can scope a rule."
              >
                <Dropdown
                  disabled={saving}
                  value={
                    form.nsDepartmentId === null
                      ? 'Every department'
                      : deptLabel(form.nsDepartmentId)
                  }
                  selectedOptions={[form.nsDepartmentId ?? '__any']}
                  onOptionSelect={(_, d) =>
                    patch({
                      nsDepartmentId: d.optionValue === '__any' ? null : (d.optionValue ?? null),
                    })
                  }
                >
                  <Option value="__any" text="Every department">
                    Every department
                  </Option>
                  {scopeDepartments.map((dept) => (
                    <Option key={dept.id} value={dept.nsDepartmentId as string} text={dept.name}>
                      {dept.name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            </>
          )}

          <Field
            label="Why this exception exists"
            required
            hint="Required. An unexplained pay override reads as a mistake once whoever made it has moved on."
          >
            <Textarea
              disabled={saving}
              value={form.note}
              rows={3}
              onChange={(_, d) => patch({ note: d.value })}
            />
          </Field>

          <Checkbox
            disabled={saving}
            checked={form.active}
            label="Active (applied to pay)"
            onChange={(_, d) => patch({ active: d.checked === true })}
          />

          {bladeError !== null ? (
            <Text className={styles.error} role="alert">
              {bladeError}
            </Text>
          ) : null}
          {bladeError === null && formProblem !== null ? (
            <div className={styles.banner}>
              <Text>{formProblem}</Text>
            </div>
          ) : null}

          <div className={styles.actions}>
            <Button appearance="secondary" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" type="submit" disabled={saving || formProblem !== null}>
              {saving ? <Spinner size="tiny" /> : editing === null ? 'Add exception' : 'Save'}
            </Button>
          </div>
        </form>
      </SideBlade>
    </div>
  );
}
